// API service for data connector operations

import axios, { AxiosResponse } from 'axios';
import {
  ConnectorJob,
  ConnectorTemplate,
  ConnectorCapability,
  JobExecutionResponse,
  JobStatusResponse,
  JobListResponse,
  ConnectionTestResult,
  JobResultsResponse,
  ConnectorStatistics,
  ListConnectorsRequest,
  CreateConnectorRequest,
  UpdateConnectorRequest,
  JobExecutionRequest
} from '../types/connector';

// Get API base URL dynamically based on environment
const getApiBaseUrl = (): string => {
  // Check for environment variable first (production)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For development/local - use current origin with /api/v1 path
  // This leverages the nginx proxy configuration
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api/v1`;
  }
  
  // Fallback for SSR or edge cases
  return '/api/v1';
};

// Create axios instance with dynamic base URL
const createApiInstance = () => {
  const baseURL = getApiBaseUrl();
  console.log('API Base URL:', baseURL);
  
  const apiInstance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Add auth token to requests if available
  apiInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor for error handling
  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Handle unauthorized access
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return apiInstance;
};

// Get API instance (lazy-loaded)
const getApi = () => createApiInstance();


export class ConnectorApiService {
  // Connector Job Management

  async listConnectors(params: ListConnectorsRequest = {}): Promise<JobListResponse> {
    const api = getApi();
    const response: AxiosResponse<JobListResponse> = await api.get('/connectors/', {
      params: {
        status: params.status,
        connector_type: params.connectorType,
        page: params.page || 1,
        page_size: params.pageSize || 50
      }
    });
    return response.data;
  }

  async createConnector(job: CreateConnectorRequest): Promise<ConnectorJob> {
    const api = getApi();
    const response: AxiosResponse<ConnectorJob> = await api.post('/connectors/', job);
    return response.data;
  }

  async getConnector(jobId: string): Promise<ConnectorJob> {
    const api = getApi();
    const response: AxiosResponse<ConnectorJob> = await api.get(`/connectors/${jobId}`);
    return response.data;
  }

  async updateConnector(jobId: string, updates: UpdateConnectorRequest): Promise<ConnectorJob> {
    const api = getApi();
    const response: AxiosResponse<ConnectorJob> = await api.put(`/connectors/${jobId}`, updates);
    return response.data;
  }

  async deleteConnector(jobId: string): Promise<{ message: string }> {
    const api = getApi();
    const response = await api.delete(`/connectors/${jobId}`);
    return response.data;
  }

  // Job Execution

  async testConnection(jobId: string): Promise<ConnectionTestResult> {
    const api = getApi();
    const response: AxiosResponse<ConnectionTestResult> = await api.post(`/connectors/${jobId}/test`);
    return response.data;
  }

  async startJob(jobId: string, request?: Omit<JobExecutionRequest, 'job_id'>): Promise<JobExecutionResponse> {
    const api = getApi();
    const response: AxiosResponse<JobExecutionResponse> = await api.post(
      `/connectors/${jobId}/start`, 
      request ? { job_id: jobId, ...request } : undefined
    );
    return response.data;
  }

  async pauseJob(jobId: string): Promise<{ message: string }> {
    const api = getApi();
    const response = await api.post(`/connectors/${jobId}/pause`);
    return response.data;
  }

  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    const api = getApi();
    const response: AxiosResponse<JobStatusResponse> = await api.get(`/connectors/${jobId}/status`);
    return response.data;
  }

  async getJobResults(
    jobId: string, 
    runId?: string, 
    limit = 100, 
    offset = 0
  ): Promise<JobResultsResponse> {
    const api = getApi();
    const response: AxiosResponse<JobResultsResponse> = await api.get(`/connectors/${jobId}/results`, {
      params: {
        run_id: runId,
        limit,
        offset
      }
    });
    return response.data;
  }

  // Template Management

  async listTemplates(publicOnly = false): Promise<ConnectorTemplate[]> {
    const api = getApi();
    const response: AxiosResponse<ConnectorTemplate[]> = await api.get('/connectors/templates/', {
      params: { public_only: publicOnly }
    });
    return response.data;
  }

  async saveTemplate(template: Omit<ConnectorTemplate, 'id' | 'user_id' | 'created_at' | 'usage_count'>): Promise<ConnectorTemplate> {
    const api = getApi();
    const response: AxiosResponse<ConnectorTemplate> = await api.post('/connectors/templates/', template);
    return response.data;
  }

  async deleteTemplate(templateId: string): Promise<{ message: string }> {
    const api = getApi();
    const response = await api.delete(`/connectors/templates/${templateId}`);
    return response.data;
  }

  // System Information

  async getCapabilities(): Promise<ConnectorCapability[]> {
    const api = getApi();
    const response: AxiosResponse<ConnectorCapability[]> = await api.get('/connectors/capabilities/');
    return response.data;
  }

  async getConnectorTypes(): Promise<{ connector_types: any[], total: number }> {
    const api = getApi();
    const response = await api.get('/connectors/types/');
    return response.data;
  }

  async getStatistics(): Promise<{ statistics: ConnectorStatistics, generated_at: string, scope: string }> {
    const api = getApi();
    const response = await api.get('/connectors/statistics/');
    return response.data;
  }

  // Utility methods

  async validateCronExpression(expression: string): Promise<{
    valid: boolean;
    cron_expression: string;
    next_runs?: string[];
    description?: string;
    error?: string;
  }> {
    // This would be a utility endpoint to validate cron expressions
    // For now, implement basic client-side validation
    try {
      // Simple validation - real implementation would call backend
      const parts = expression.trim().split(/\s+/);
      if (parts.length !== 5) {
        throw new Error('Cron expression must have 5 parts');
      }
      
      return {
        valid: true,
        cron_expression: expression,
        description: 'Custom schedule'
      };
    } catch (error) {
      return {
        valid: false,
        cron_expression: expression,
        error: error instanceof Error ? error.message : 'Invalid expression'
      };
    }
  }

  // Helper method to format file sizes
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Helper method to format processing time
  formatProcessingTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  // Helper method to get relative time
  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = (now.getTime() - date.getTime()) / 1000;

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
}

// Export singleton instance
export const connectorApi = new ConnectorApiService();